import React, { useEffect, useState } from 'react';
import { Table, Button, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { ActionButtons } from '../../common';
import { theme } from '../../../theme';
import ObjetoModal from './ObjetoModal';

interface ObjetoData {
  id: number;
  descripcion: string;
  tipo: number;
  clasificacion: number;
  clasificacion_texto: string;
}


const ListarObjetos: React.FC = () => {
  const { user } = useAuth0();
  const empresa = user?.id_empresa;
  const [data, setData] = useState<ObjetoData[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [objetoEditar, setObjetoEditar] = useState<{ id: number; descripcion: string; clasificacion: number } | null>(null);
  const { getAccessTokenSilently , isLoading } = useAuth0();

  const getTipoText = (tipo: number) => {
    switch (tipo) {
      case 1:
        return 'Grupo';
      case 2:
        return 'Subgrupo';
      case 3:
        return 'Marca';
      default:
        return 'Desconocido';
    }
  };


  const desired_output = (data: any, column: any) => {
    const unique_values = data
        .map((item: any) => item[column])
        .filter(
            (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
        );
    return unique_values;
  };
  

  const clasificacionesUnicas = desired_output(data, 'clasificacion_texto')
  .map((clasificacion: any) => ({text: clasificacion, value: clasificacion}));


  const columns: ColumnsType<ObjetoData> = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>ID</span>,
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Descripción</span>,
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Tipo</span>,
      dataIndex: 'clasificacion_texto',
      key: 'clasificacion_texto',
      align: 'center' as const,
      width: 150,
      filters: clasificacionesUnicas,
      onFilter: (value, record) => record.clasificacion_texto == value,
      render: (text: string) => (
        <span style={{ 
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 600,
          backgroundColor: text === 'Marca' ? '#e6f7ff' : text === 'Grupo' ? '#f6ffed' : '#fff7e6',
          color: text === 'Marca' ? '#1890ff' : text === 'Grupo' ? '#52c41a' : '#fa8c16'
        }}>
          {text}
        </span>
      )
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Acciones</span>,
      key: 'acciones',
      align: 'center' as const,
      width: 120,
      render: (_, record) => (
        <ActionButtons
          onEdit={() => handleEditar(record)}
          onDelete={() => handleEliminar(record.id)}
          tooltips={{
            edit: 'Editar objeto',
            delete: 'Eliminar objeto'
          }}
        />
      ),
    }
  ];

    const fetchObjetos = async () => {
    setLoading(true);
    try {
      console.log('empresa',empresa)
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_objetos/${empresa}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar los objetos ');
      }
      
      const result = await response.json();
      setData(result);
      console.log('result',result);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los objetos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    fetchObjetos();
  }, [isLoading, empresa]);

  const handleNuevo = () => {
    setObjetoEditar(null);
    setModalVisible(true);
  };

  const handleEditar = (objeto: ObjetoData) => {
    setObjetoEditar({ 
      id: objeto.id, 
      descripcion: objeto.descripcion,
      clasificacion: objeto.clasificacion || objeto.tipo
    });
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setObjetoEditar(null);
  };

  const handleModalSuccess = () => {
    fetchObjetos(); // Recargar lista
  };

  const handleEliminar = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este objeto?')) {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/eliminar_objeto/${id}/${empresa}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        
        if (!response.ok) {
          throw new Error('Error al eliminar el objeto');
        }
        
        message.success('Objeto eliminado correctamente');
        fetchObjetos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        message.error('Error al eliminar el objeto');
      }
    }
  };

  return (
    <div>
      {/* Botón Nuevo arriba */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <Button 
          type='primary'
          onClick={handleNuevo}
          style={{
            backgroundColor: theme.colors.secondary,
            borderColor: theme.colors.secondary,
            fontWeight: 500
          }}
        >
          Nuevo Objeto
        </Button>
      </div>
      
      {/* Tabla con acciones inline */}
      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          total: data.length,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} registros`,
        }}
      />

      {/* Modal para crear/editar */}
      <ObjetoModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        objeto={objetoEditar}
      />
    </div>
  );
};

export default ListarObjetos; 